import { selecionarFormulasPorCriterio } from '@shared/components/formula/utils/formula-contexto.util';
import { coletarEstadosFormulas } from '@shared/components/formula/utils/formula-estados.util';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AtivacaoSectionCardComponent } from '@shared/components/ativacao-section-card/ativacao-section-card.component';
import { IssqnTributoObservacoesSectionComponent } from './sections/tributo-observacoes-section/tributo-observacoes-section.component';
import { IssqnTributoObservacoesFiscoSectionComponent } from './sections/tributo-observacoes-fisco-section/tributo-observacoes-fisco-section.component';
import { IssqnTributoRegraSectionComponent, MAPA_CAMPOS_ISS_NATUREZA } from './sections/tributo-regra-section/tributo-regra-section.component';
import { IssqnTributoExcecoesSectionComponent } from './sections/tributo-excecoes-section/tributo-excecoes-section.component';
import { FormulaEditorDialogComponent, FormulaEditorDialogData, FormulaEditorDialogResult } from '@shared/components/formula/formula-editor-dialog/formula-editor-dialog.component';
import { VariaveisDisponiveisDialogComponent, VariaveisDisponiveisDialogData, VariaveisCursorPayload } from '@shared/components/formula/variaveis-disponiveis-dialog/variaveis-disponiveis-dialog.component';
import { NaturezaOperacaoIssqnExcecaoPendenteStruct } from '@shared/services/structs/motor-fiscal/natureza-operacao-issqn-excecao-pendente.struct';
import { FormulaItemStruct } from '@shared/services/structs/formula/formula-item.struct';
import { CstIssqnStruct } from '@shared/services/structs/motor-fiscal/cst-issqn.struct';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';
import { TipoFormulaEnum } from '@shared/enums/formula/tipo-formula.enum';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { FormulaCatalogoService } from '@shared/services/apis/motor-fiscal/formula-catalogo.service';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import {
    aplicarObrigatoriedadeIndicadores,
    lerIndicadoresCst,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';

@Component({
    selector: 'app-natureza-operacao-issqn-tab',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        AtivacaoSectionCardComponent,
        IssqnTributoRegraSectionComponent,
        IssqnTributoObservacoesSectionComponent,
        IssqnTributoObservacoesFiscoSectionComponent,
        IssqnTributoExcecoesSectionComponent,
    ],
    templateUrl: './natureza-operacao-issqn-tab.component.html',
    styleUrl: './natureza-operacao-issqn-tab.component.css',
})
export class NaturezaOperacaoIssqnTabComponent implements OnInit {
    @Input({ required: true }) form!: FormGroup;
    @Input() codigoTributos: CstIssqnStruct[] = [];
    @Input() modalidadesBcSt: ModalidadeBcOption[] = [];
    @Input() indicadoresIncentivoFiscalIssqn: LookupItemStruct[] = [];
    @Input() formulasIssqn: FormulaCatalogoItemStruct[] = [];
    @Input() estadosFormulas: FormulaItemStruct[] = [];
    @Input() idNaturezaOperacao: number | null = null;
    @Input() excecoesPendentes: NaturezaOperacaoIssqnExcecaoPendenteStruct[] = [];
    @Output() excecoesChange = new EventEmitter<NaturezaOperacaoIssqnExcecaoPendenteStruct[]>();


    ngOnInit(): void {
        this.isAtivoControl.valueChanges.subscribe(() => this.sincronizarObrigatoriedade());
        this.issqnForm.get('indicadorIss')?.valueChanges.subscribe(() => this.sincronizarObrigatoriedade());
        this.sincronizarObrigatoriedade();
    }

    /** Indicador, alíquota e incentivo exigidos com o ISSQN ligado; os campos do indicador, pelo catálogo. */
    private sincronizarObrigatoriedade(): void {
        const ativo = !!this.isAtivoControl.value;
        const codigo = this.issqnForm.get('indicadorIss')?.value;
        ['indicadorIss', 'vAliq', 'indIncentivo'].forEach(nome => this.aplicarObrigatorio(this.issqnForm.get(nome), ativo));
        const indicadores = ativo && codigo ? lerIndicadoresCst(this.codigoTributos.find(c => c.codigo === codigo)?.indicadores) : null;
        aplicarObrigatoriedadeIndicadores(this.issqnForm, indicadores, MAPA_CAMPOS_ISS_NATUREZA);
    }

    private aplicarObrigatorio(control: import('@angular/forms').AbstractControl | null | undefined, obrigatorio: boolean): void {
        if (!control) return;
        if (obrigatorio) {
            control.addValidators(Validators.required);
        } else {
            control.removeValidators(Validators.required);
        }
        control.updateValueAndValidity({ emitEvent: false });
    }

    public get issqnForm(): FormGroup {
        return this.form.get('issqn') as FormGroup;
    }

    public get isAtivoControl(): FormControl {
        return this.issqnForm.get('isAtivo') as FormControl;
    }

    public get indicadorIssValue(): string | null {
        return this.issqnForm?.get('indicadorIss')?.value ?? null;
    }

    /** Tipo de operação da natureza (Entrada/Saída), lido do form raiz. */
    public get tipoOperacaoValue(): TipoOperacaoEnum {
        return this.form?.get('tipoOperacao')?.value ?? TipoOperacaoEnum.Saida;
    }

    public get formulasParaCodigoTributo(): FormulaCatalogoItemStruct[] {
        if (!this.indicadorIssValue) return this.formulasIssqn;
        return selecionarFormulasPorCriterio(this.formulasIssqn, 'codigo_tributo_issqn', this.indicadorIssValue);
    }

    /** Fonte única em memória das fórmulas: o FormGroup, que o salvar() também lê.
        O @Input estadosFormulas serve só de seed inicial (o pai já popula o control). */
    private get estadosFormulasMemoria(): FormulaItemStruct[] {
        return (this.issqnForm.get('_estadosFormulas')?.value as FormulaItemStruct[]) ?? [];
    }

    public get personalizadasCount(): number {
        return this.estadosFormulasMemoria.filter(e => e.tipo === TipoFormulaEnum.Personalizada).length;
    }

    /** Variáveis únicas (por tag) disponíveis nas fórmulas do SN. */
    public get variaveisUnicas(): VariavelStruct[] {
        const todas = this.formulasIssqn.flatMap(f => f.variaveis);
        return todas.filter((v, i, arr) => arr.findIndex(x => (x.chave || x.tagXml || x.nome) === (v.chave || v.tagXml || v.nome)) === i);
    }

    constructor(
        private dialog: MatDialog,
        private formulaCatalogoService: FormulaCatalogoService,
    ) {}

    public abrirEditorFormulas(): void {
        const codigoTributo = this.codigoTributos.find(c => c.codigo === this.indicadorIssValue);
        const data: FormulaEditorDialogData = {
            titulo: ['ISSQN', this.indicadorIssValue, codigoTributo?.descricao].filter(Boolean).join(' - '),
            formulas: this.formulasParaCodigoTributo,
            estadosIniciais: this.estadosFormulasMemoria,
            estadosFormulasAgregados: coletarEstadosFormulas(this.form),
            contextoRaiz: 'natureza-operacao',
            validarPersonalizacoes: request => this.formulaCatalogoService.validarPersonalizacoes(request),
        };

        const ref = this.dialog.open(FormulaEditorDialogComponent, {
            width: '900px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });

        ref.afterClosed().subscribe((result: FormulaEditorDialogResult | null) => {
            if (result) {
                this.issqnForm.get('_estadosFormulas')?.setValue(result.formulas);
            }
        });
    }

    public abrirVariaveisDisponiveis(payload: VariaveisCursorPayload): void {
        const editor = payload.editor;
        const data: VariaveisDisponiveisDialogData = {
            variaveis: this.variaveisUnicas,
            targetControl: payload.control,
            cursorStart: payload.selectionStart,
            cursorEnd: payload.selectionEnd,
            onInsert: editor ? (v: VariavelStruct) => editor.inserirVariavel(v) : undefined,
        };

        this.dialog.open(VariaveisDisponiveisDialogComponent, {
            width: '820px',
            maxWidth: '96vw',
            maxHeight: '90vh',
            data,
        });
    }
}
